import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Euro, Check, Trophy, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";

/**
 * GuessPrice — public player UI for guess_price games.
 *
 * Player sees a product/window image and enters a price guess in EUR.
 * Winner is decided later by the admin via the "Draw winners" dialog.
 */
export default function GuessPrice({
  game,
  participant,
  ctasCompleted,
  receipt,
  onAlreadyParticipated,
  onSubmitted,
}) {
  const { t } = useTranslation();
  const [price, setPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyEntered, setAlreadyEntered] = useState(false);

  async function submit() {
    const n = parseFloat(price);
    if (isNaN(n) || n < 0) {
      toast.error(t("play.gp_required"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/price-submit`,
        {
          participant,
          ctas_completed: ctasCompleted || [],
          price_guess: n,
          fingerprint: getClientFingerprint(),
          receipt_url: receipt?.url || null,
          receipt_ocr: receipt?.ocr || null,
        }
      );
      setAlreadyEntered(!!data.already_entered);
      setDone(true);
      onSubmitted?.();
      toast.success(
        data.already_entered ? t("play.gp_already") : t("play.gp_confirmed_title")
      );
    } catch (e) {
      if (isAlreadyParticipatedError(e)) {
        onAlreadyParticipated?.(alreadyParticipatedMessage(e));
      } else {
        toast.error(extractErrorMessage(e, "Error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center" data-testid="gp-confirmed">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-600 mb-4">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-bold tracking-tight mb-2">
          {alreadyEntered
            ? t("play.gp_already")
            : t("play.gp_confirmed_title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("play.gp_confirmed_body")}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-violet-500" />
          Your guess: <span className="text-foreground">{price} €</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-xl mx-auto" data-testid="gp-form">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-300 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] mb-2">
          <Trophy className="h-3 w-3" />
          {t("play.gp_badge")}
        </div>
        <h3 className="text-xl font-bold tracking-tight">
          {t("play.gp_title")}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          {t("play.gp_body")}
        </p>
      </div>

      {game.price_image ? (
        <div className="rounded-2xl overflow-hidden border border-border shadow-sm bg-muted/10">
          <img
            src={game.price_image}
            alt="Guess the price"
            className="w-full h-auto max-h-[460px] object-contain bg-gradient-to-br from-muted/40 to-muted/20"
            data-testid="gp-product-image"
          />
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          (no image)
        </div>
      )}

      <div className="qrise-card p-5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
          {t("play.gp_input_label")}
        </label>
        <div className="relative">
          <Input
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            className="h-16 rounded-xl text-center text-3xl font-extrabold tracking-tight pr-12"
            data-testid="gp-price-input"
          />
          <Euro className="h-6 w-6 absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={submitting || !price}
        className="w-full h-12 rounded-xl text-sm font-bold uppercase tracking-wider bg-foreground text-background hover:bg-foreground/90"
        data-testid="gp-submit-btn"
      >
        <Trophy className="h-4 w-4 mr-2" />
        {submitting ? t("play.gp_submitting") : t("play.gp_submit")}
      </Button>
    </div>
  );
}
